import type { StateCreator } from "zustand";
import type { Tournament } from "@/campaigns/modules/tournament/types";
import type {
  LeaderboardEntrySchema,
  TournamentLeaderboardsSchema,
} from "@/types/generated-types";
import {
  campaignKey,
  matchesKey,
  type CampaignKey,
} from "@/campaigns/campaign-key";
import type { CampaignStreamState } from "./campaigns.store";
import type { AppStore } from "./app.store";

/**
 * The tournament layer, mirroring the backend split (and symmetric with the
 * prize-drop slice):
 *  - `tournaments`                ← assembled from `subscribe:campaigns` (structural)
 *  - `tournamentLeaderboardsById` ← `subscribe:tournament_leaderboards` (volatile board)
 *
 * The leaderboard is kept in its OWN raw slice — never folded into the assembled
 * `Tournament` — so a ~5s leaderboard tick re-renders only the leaderboard subtree,
 * not the whole detail view (see `modules/tournament/types.ts`). The player's own
 * score/position still rides the per-player `tournament_progress` stream.
 */
export interface TournamentSlice {
  /** Assembled structural view objects (campaigns, `type` "TO" or "RA"), rebuilt on a
   *  `campaigns` push via `recomputeTournaments`. */
  tournaments: Tournament[];
  setTournaments: (tournaments: Tournament[]) => void;
  clearTournaments: () => void;

  /** Raw `tournament_leaderboards` stream, keyed by campaign key (the volatile tail). */
  tournamentLeaderboardsById: Record<CampaignKey, TournamentLeaderboardsSchema>;
  tournamentLeaderboardsStream: CampaignStreamState;
  setTournamentLeaderboards: (boards: TournamentLeaderboardsSchema[]) => void;
  setTournamentLeaderboardsSubscribed: (subscribed: boolean) => void;
  setTournamentLeaderboardsLoading: (loading: boolean) => void;
  clearTournamentLeaderboards: () => void;
}

const initialStream: CampaignStreamState = {
  subscribed: false,
  loading: false,
};

export const createTournamentSlice: StateCreator<TournamentSlice> = (set) => ({
  tournaments: [],
  setTournaments: (tournaments) => set({ tournaments }),
  clearTournaments: () => set({ tournaments: [] }),

  tournamentLeaderboardsById: {},
  tournamentLeaderboardsStream: initialStream,
  setTournamentLeaderboards: (boards) =>
    set(() => {
      const byId: Record<CampaignKey, TournamentLeaderboardsSchema> = {};
      for (const b of boards) byId[campaignKey(b)] = b;
      return { tournamentLeaderboardsById: byId };
    }),
  setTournamentLeaderboardsSubscribed: (subscribed) =>
    set((state) => ({
      tournamentLeaderboardsStream: {
        ...state.tournamentLeaderboardsStream,
        subscribed,
        loading: false,
      },
    })),
  setTournamentLeaderboardsLoading: (loading) =>
    set((state) => ({
      tournamentLeaderboardsStream: {
        ...state.tournamentLeaderboardsStream,
        loading,
      },
    })),
  clearTournamentLeaderboards: () =>
    set({
      tournamentLeaderboardsById: {},
      tournamentLeaderboardsStream: initialStream,
    }),
});

/** Resolve an assembled tournament by custom_id or numeric id (as string). */
export function selectTournamentById(
  state: AppStore,
  key: string
): Tournament | undefined {
  return state.tournaments.find((t) => matchesKey(t.campaign, key));
}

/** The competitor leaderboard rows for a tournament campaign (the volatile slice).
 *  Read this directly — it is NOT on the assembled `Tournament`. */
export function selectTournamentLeaderboardByCampaignId(
  state: AppStore,
  key: string
): LeaderboardEntrySchema[] | undefined {
  const board = Object.values(state.tournamentLeaderboardsById).find((b) =>
    matchesKey(b, key)
  );
  return board?.leaderboard;
}
